// Survey chrome renderer. Builds a shadow-DOM popup, wires up answer
// capture, and emits results back via callbacks. Self-contained — no
// consumer-app design system dependency.
import type { SurveyAnswer, SurveyDefinition, SurveyResponse } from '@layers/core-wasm';

import { SURVEY_STYLES } from './styles.js';
import { type QuestionTemplate, renderQuestion } from './templates.js';

export interface SurveyRendererCallbacks {
  /** Fires when chrome appears on screen. */
  onShown: (surveyId: string) => void;
  /** Fires when the user dismisses without responding. */
  onDismissed: (surveyId: string) => void;
  /** Fires when the user submits — the SDK forwards `response` to Rust. */
  onSubmit: (surveyId: string, response: SurveyResponse) => void;
}

/**
 * One renderer manages one survey at a time. Calling `show()` while a survey
 * is already on screen is a no-op (we don't queue or stack popups in MVP).
 */
export class SurveyRenderer {
  private host: HTMLElement | null = null;
  private root: ShadowRoot | null = null;
  private current: SurveyDefinition | null = null;
  private templates: QuestionTemplate[] = [];
  private shownAt: string | null = null;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  private escHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(private readonly callbacks: SurveyRendererCallbacks) {}

  isShowing(): boolean {
    return this.host !== null;
  }

  currentSurveyId(): string | null {
    return this.current?.id ?? null;
  }

  /**
   * Render the given survey. If chrome is already up for this or another
   * survey, this is a no-op (returns false). Returns true on actual render.
   */
  show(survey: SurveyDefinition): boolean {
    if (typeof document === 'undefined') {
      // Non-browser env (SSR / Node).
      return false;
    }
    if (this.host) return false;

    const host = document.createElement('layers-survey');
    host.classList.add(`am-position-${survey.display?.position ?? 'bottom-right'}`);
    document.body.appendChild(host);

    const root = host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = SURVEY_STYLES;
    root.appendChild(style);

    const popup = document.createElement('div');
    popup.className = 'am-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-label', survey.name);

    const dismissable = survey.display?.dismissable !== false;
    const closeBtn = dismissable
      ? `<button type="button" class="am-close" aria-label="Dismiss survey" title="Dismiss">×</button>`
      : '';

    this.templates = (survey.questions ?? []).map((q) => renderQuestion(q));

    popup.innerHTML = `
      <div class="am-header">
        <p class="am-title">${escapeHtml(survey.name)}</p>
        ${closeBtn}
      </div>
      <div class="am-body">${this.templates.map((t) => t.html).join('')}</div>
      <div class="am-actions">
        <button type="button" class="am-btn-submit" disabled>Submit</button>
      </div>
    `;
    root.appendChild(popup);

    this.host = host;
    this.root = root;
    this.current = survey;
    this.shownAt = new Date().toISOString();

    this.wireScaleButtons();
    this.wireStarButtons();
    this.wireMultipleChoice();
    this.wireOpenText();
    this.wireLink();
    this.wireSubmit();

    if (dismissable) {
      this.wireDismissAffordances();
    }

    this.callbacks.onShown(survey.id);
    return true;
  }

  /** Manually dismiss the current survey (programmatic). Fires onDismissed. */
  dismiss(): void {
    if (!this.current) return;
    const { id } = this.current;
    this.teardown();
    this.callbacks.onDismissed(id);
  }

  /** Force-close the current survey without firing any callback. */
  destroy(): void {
    this.teardown();
  }

  private teardown(): void {
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler, true);
      this.outsideClickHandler = null;
    }
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.host && this.host.parentNode) {
      this.host.parentNode.removeChild(this.host);
    }
    this.host = null;
    this.root = null;
    this.current = null;
    this.templates = [];
    this.shownAt = null;
  }

  // ── DOM wiring ───────────────────────────────────────────────────────

  private wireScaleButtons(): void {
    if (!this.root) return;
    const buttons = this.root.querySelectorAll<HTMLButtonElement>('.am-scale-btn');
    for (const btn of Array.from(buttons)) {
      btn.addEventListener('click', () => {
        const q = btn.getAttribute('data-q');
        if (!q || !this.root) return;
        // Clear sibling selection.
        const siblings = this.root.querySelectorAll<HTMLButtonElement>(
          `.am-scale-btn[data-q="${cssEscape(q)}"]`
        );
        siblings.forEach((s) => s.classList.remove('am-selected'));
        btn.classList.add('am-selected');
        this.refreshSubmitState();
      });
    }
  }

  private wireStarButtons(): void {
    if (!this.root) return;
    const buttons = this.root.querySelectorAll<HTMLButtonElement>('.am-star-btn');
    for (const btn of Array.from(buttons)) {
      btn.addEventListener('click', () => {
        const q = btn.getAttribute('data-q');
        const v = Number(btn.getAttribute('data-v') ?? '0');
        if (!q || !this.root) return;
        const siblings = this.root.querySelectorAll<HTMLButtonElement>(
          `.am-star-btn[data-q="${cssEscape(q)}"]`
        );
        siblings.forEach((s) => {
          const sv = Number(s.getAttribute('data-v') ?? '0');
          s.classList.toggle('am-selected', sv <= v);
        });
        this.refreshSubmitState();
      });
    }
  }

  private wireMultipleChoice(): void {
    if (!this.root) return;
    const inputs = this.root.querySelectorAll<HTMLInputElement>(
      '.am-mc-options input[type="radio"]'
    );
    for (const input of Array.from(inputs)) {
      input.addEventListener('change', () => this.refreshSubmitState());
    }
  }

  private wireOpenText(): void {
    if (!this.root) return;
    const tas = this.root.querySelectorAll<HTMLTextAreaElement>('.am-textarea');
    for (const ta of Array.from(tas)) {
      ta.addEventListener('input', () => this.refreshSubmitState());
    }
  }

  private wireLink(): void {
    if (!this.root || !this.current) return;
    const links = this.root.querySelectorAll<HTMLAnchorElement>('.am-cta-link');
    for (const link of Array.from(links)) {
      link.addEventListener('click', () => {
        // Treat tap as immediate submit — link surveys have no other UI.
        // We schedule a microtask so the navigation isn't blocked.
        queueMicrotask(() => this.submit());
      });
    }
  }

  private wireSubmit(): void {
    if (!this.root) return;
    const submitBtn = this.root.querySelector<HTMLButtonElement>('.am-btn-submit');
    submitBtn?.addEventListener('click', () => this.submit());
  }

  private wireDismissAffordances(): void {
    if (!this.root || !this.host) return;
    const closeBtn = this.root.querySelector<HTMLButtonElement>('.am-close');
    closeBtn?.addEventListener('click', () => this.dismiss());

    // Outside-click — capture phase so it fires before page handlers.
    this.outsideClickHandler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!this.host || !target) return;
      if (!this.host.contains(target)) {
        this.dismiss();
      }
    };
    document.addEventListener('click', this.outsideClickHandler, true);

    // ESC to dismiss
    this.escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.dismiss();
    };
    document.addEventListener('keydown', this.escHandler);
  }

  // ── Submit / answer collection ───────────────────────────────────────

  private collectAnswers(): SurveyAnswer[] | null {
    if (!this.current || !this.root) return null;
    const out: SurveyAnswer[] = [];
    const questions = this.current.questions ?? [];
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const tpl = this.templates[i];
      if (q && tpl) {
        const answer = tpl.read(this.root);
        if (q.required && (answer === null || answer === undefined || answer === '')) {
          return null;
        }
        // Schema constrains answer to scalar | array | null — coerce undefined to null.
        out.push({
          question_id: q.id,
          answer: (answer ?? null) as SurveyAnswer['answer']
        });
      }
    }
    return out;
  }

  private refreshSubmitState(): void {
    if (!this.root) return;
    const submitBtn = this.root.querySelector<HTMLButtonElement>('.am-btn-submit');
    if (!submitBtn) return;
    const answers = this.collectAnswers();
    submitBtn.disabled = answers === null;
  }

  private submit(): void {
    if (!this.current) return;
    const answers = this.collectAnswers();
    if (answers === null) {
      // Required question(s) unanswered — refuse silently. Submit button
      // should already be disabled but this is a defensive check.
      return;
    }
    const surveyId = this.current.id;
    const response: SurveyResponse = {
      answers,
      completed_at: new Date().toISOString()
    };
    if (this.shownAt) {
      response.shown_at = this.shownAt;
    }
    this.teardown();
    this.callbacks.onSubmit(surveyId, response);
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function cssEscape(s: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(s);
  }
  return s.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}
